import React, { useState, useEffect, useRef } from 'react';
import ComboTypePicker from 'components/show_drawing/redraw_form/ComboTypePicker.jsx';
import ComboGuestMaterialChooser from 'components/show_drawing/redraw_form/ComboGuestMaterialChooser';
import renderOption from 'components/show_drawing/redraw_form/options/renderOption.jsx';
import Constants from 'components/show_drawing/Constants.jsx';
import { readStoredFormulaOptions } from 'components/show_drawing/redraw_form/combo_local_storage.js';
import { relationHasRenderableOptions, visibleAskOptions } from 'components/show_drawing/redraw_form/combo_guest_helpers.js';
import styles from 'components/show_drawing/redraw_form/_style.module.scss';

const NONE_VALUE = '';
const SELECT_VALUE = '__select__';

// Guest ask options do not participate in the host's user-changed tracking
// or recommendation engine. When that changes, wire up a guest-scoped
// tracker instead of these no-ops.
const NO_OP = () => {};
const EMPTY_LIST = [];

const findActiveRelations = (combos, activeCombosByGuestName) => (
  (combos || []).flatMap((group) => {
    const activeId = activeCombosByGuestName[group.guest_name];
    const activeRelation = activeId
      ? group.relations.find((relation) => relation.id === activeId)
      : null;
    return activeRelation ? [{ guestName: group.guest_name, relation: activeRelation }] : [];
  })
);

const rehydrateActiveSlots = (activeRelations, drawingMainId, unit) => {
  const stored = readStoredFormulaOptions(drawingMainId, unit);
  if (!stored) { return; }

  activeRelations.forEach(({ relation }) => {
    (relation.ask_options || []).forEach((option) => {
      const storedValue = stored[`ask_values[${relation.id}][${option.name}]`];
      if (storedValue === undefined || storedValue === null || storedValue === '') { return; }
      emitEvent(Constants.drawing_option_set_value, {
        id: relation.guest_drawing.id,
        name: option.name,
        value: storedValue
      });
    });
  });
};

const ComboButtonsSection = ({ combos, activeCombosByGuestName, setActiveCombosByGuestName, setRedrawRequested, unit, register, errors, drawingMainId, advancedOptionsVisible }) => {
  const [pickerGuestName, setPickerGuestName] = useState(null);
  const [collapsedByGuestName, setCollapsedByGuestName] = useState({});

  const activeRelations = findActiveRelations(combos, activeCombosByGuestName);
  const activeRelationKey = activeRelations
    .map(({ guestName, relation }) => `${guestName}:${relation.id}`)
    .sort()
    .join(',');

  // Re-hydrate guest ask_values from localStorage once per combo activation, so a
  // refresh (or reactivating the same combo) starts with the last submitted values
  // instead of option.default_value. The ref-then-effect pattern keeps the effect
  // body free of stale-closure risk: deps fire on activation changes only, but the
  // body always reads the latest list of active relations.
  const activeRelationsRef = useRef(activeRelations);
  activeRelationsRef.current = activeRelations;
  useEffect(() => {
    if (activeRelationKey === '') { return; }
    rehydrateActiveSlots(activeRelationsRef.current, drawingMainId, unit);
  }, [activeRelationKey, drawingMainId, unit]);

  if (!combos || combos.length === 0) { return null; }

  const handleChange = (group, event) => {
    const value = event.target.value;

    if (value === SELECT_VALUE) {
      setPickerGuestName(group.guest_name);
      return;
    }

    if (value === NONE_VALUE) {
      setActiveCombosByGuestName((previous) => {
        const { [group.guest_name]: _removed, ...rest } = previous;
        return rest;
      });
      return;
    }

    const relationId = parseInt(value, 10);
    setActiveCombosByGuestName((previous) => ({
      ...previous,
      [group.guest_name]: relationId
    }));
  };

  const toggleCollapsed = (guestName) => {
    setCollapsedByGuestName((previous) => ({
      ...previous,
      [guestName]: !previous[guestName]
    }));
  };

  const pickerGroup = pickerGuestName
    ? combos.find((group) => group.guest_name === pickerGuestName)
    : null;

  return (
    <div className={styles.combo_buttons_section}>
      {combos.map((group) => {
        const activeId = activeCombosByGuestName[group.guest_name];
        const activeRelation = activeId
          ? group.relations.find((relation) => relation.id === activeId)
          : null;
        const activeGuestLabel = activeRelation && (
          activeRelation.guest_drawing.catalog_name
          || activeRelation.guest_drawing.friendly_name
          || activeRelation.guest_drawing.name
        );
        const hasChildren = activeRelation && relationHasRenderableOptions(activeRelation);
        const expanded = hasChildren && !collapsedByGuestName[group.guest_name];

        return (
          <div key={group.guest_name} className={styles.combo_button_cell}>
            <div className={expanded ? styles.child_option : undefined}>
              <div className={styles.row}>
                <div className="input-group" style={{ width: '100%' }}>
                  <span
                    className={`d_input_add_on input-group-addon input-group-addon-${window.locale} ${styles.input_add_on}`}
                  >
                    {t(`drawing.combo_label_${group.guest_name}`)}
                  </span>
                  <select
                    value={activeRelation ? String(activeRelation.id) : NONE_VALUE}
                    onChange={(event) => handleChange(group, event)}
                    data-combo-guest-name={group.guest_name}
                    className="form-control"
                  >
                    <option value={NONE_VALUE}>{t('drawing.combo_none')}</option>
                    {activeRelation && (
                      <option value={String(activeRelation.id)}>{activeGuestLabel}</option>
                    )}
                    <option value={SELECT_VALUE}>
                      {interpolate(t('drawing.combo_select_a'), [t(`drawing.combo_label_${group.guest_name}`)])}
                    </option>
                  </select>
                </div>
                {hasChildren && (
                  <>
                    <span className="float-right">
                      <a
                        href="#"
                        onClick={(event) => { event.preventDefault(); toggleCollapsed(group.guest_name); }}
                        className={styles.more_link}
                        data-combo-toggle-guest-name={group.guest_name}
                      >
                        {!expanded && <>{t('drawing.show_child_options_cta')} <i className="fa fa-caret-down" aria-hidden="true"></i></>}
                        {expanded && <>{t('drawing.hide_child_options_cta')} <i className="fa fa-caret-up" aria-hidden="true"></i></>}
                      </a>
                    </span>
                    <div style={{ clear: 'both' }}></div>
                  </>
                )}
              </div>
              {expanded && (
                <>
                  <ComboGuestMaterialChooser
                    relation={activeRelation}
                    unit={unit}
                    register={register}
                    errors={errors}
                    setRedrawRequested={setRedrawRequested}
                    drawingMainId={drawingMainId}
                  />
                  {visibleAskOptions(activeRelation.ask_options || [], advancedOptionsVisible).map((option) => renderOption({
                    id: activeRelation.guest_drawing.id,
                    drawing: activeRelation.guest_drawing,
                    option,
                    unit,
                    optionsUserChanged: EMPTY_LIST,
                    addToOptionsUserChanged: NO_OP,
                    setRedrawRequested,
                    labelGroup: `combo_guest_${activeRelation.id}`,
                    inputName: `ask_values[${activeRelation.id}][${option.name}]`
                  }))}
                </>
              )}
            </div>
          </div>
        );
      })}

      {pickerGroup && (
        <ComboTypePicker
          group={pickerGroup}
          onSelect={(relationId) => {
            setActiveCombosByGuestName((previous) => ({
              ...previous,
              [pickerGroup.guest_name]: relationId
            }));
            // Picking a guest is an explicit "I want to configure this" action,
            // so override any prior collapsed state for this slot.
            setCollapsedByGuestName((previous) => ({
              ...previous,
              [pickerGroup.guest_name]: false
            }));
            setPickerGuestName(null);
          }}
          onClose={() => setPickerGuestName(null)}
        />
      )}
    </div>
  );
};

export default ComboButtonsSection;
